import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { ArrowClockwise, CheckCircle, X as XIcon, Clock, Lightning } from "@phosphor-icons/react";

export default function WebhookDeliveries() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [retrying, setRetrying] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = () => {
    const params = status ? { status } : {};
    return api.get("/webhook-deliveries", { params }).then(({ data }) => setItems(data));
  };
  useEffect(() => { load(); const t = setInterval(load, 20_000); return () => clearInterval(t); }, [status]);  // eslint-disable-line

  const retry = async (d) => {
    setRetrying(d.delivery_id);
    try {
      await api.post(`/webhook-deliveries/${d.delivery_id}/retry`);
      load();
    } catch (e) {
      alert("Retry failed: " + (e.response?.data?.detail || e.message));
    } finally { setRetrying(null); }
  };

  const counts = items.reduce((a, d) => { a[d.status] = (a[d.status] || 0) + 1; return a; }, {});

  return (
    <div className="space-y-5" data-testid="webhook-deliveries-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><Lightning size={12}/> Integrations</div>
          <h1 className="font-display text-[28px] font-bold tracking-tight mt-1">Webhook deliveries</h1>
          <div className="text-[12px] text-[var(--span-text-2)] mt-1">
            Outbound delivery log · auto-refresh every 20s · {items.length} recent attempts
          </div>
        </div>
        <div className="flex gap-2">
          {[
            ["", "All"],
            ["delivered", `Delivered (${counts.delivered || 0})`],
            ["failed", `Failed (${counts.failed || 0})`],
            ["pending", `Pending (${counts.pending || 0})`],
          ].map(([s, lbl]) => (
            <button key={s} data-testid={`filter-${s || "all"}`} onClick={() => setStatus(s)} className={`badge ${status === s ? "badge-success" : ""}`}>{lbl}</button>
          ))}
        </div>
      </div>

      <div className="card-flat">
        <table className="span-table">
          <thead>
            <tr><th>Event</th><th>URL</th><th>Status</th><th>HTTP</th><th>Attempts</th><th>Created</th><th>Last attempt</th><th></th></tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={8} className="p-6 text-center text-[12px] text-[var(--span-text-2)]">
                No webhook deliveries yet. Configure an outbound webhook in Developer → API Keys.
              </td></tr>
            )}
            {items.map((d) => {
              const ok = d.status === "delivered";
              const bad = d.status === "failed";
              return (
                <tr key={d.delivery_id} data-testid={`webhook-row-${d.delivery_id}`}>
                  <td className="font-mono text-[11px]">{d.event}</td>
                  <td className="font-mono text-[10px] max-w-md truncate text-[var(--span-text-2)]" title={d.url}>{d.url}</td>
                  <td>
                    <span className={`badge ${ok ? "badge-success" : bad ? "badge-danger" : "badge-info"}`}>
                      {ok ? <CheckCircle size={10}/> : bad ? <XIcon size={10}/> : <Clock size={10}/>} {d.status}
                    </span>
                  </td>
                  <td className="font-mono text-[11px]">{d.last_response_code || "—"}</td>
                  <td className="tnum">{d.attempts || 0}</td>
                  <td className="text-[var(--span-text-2)] text-[11px]">{fmtDate(d.created_at)}</td>
                  <td className="text-[var(--span-text-2)] text-[11px]">{fmtDate(d.last_attempt_at)}</td>
                  <td className="flex gap-1.5">
                    <button data-testid={`view-${d.delivery_id}`} onClick={() => setDetail(d)} className="text-[11px] text-[var(--span-text-2)] hover:text-black underline">View</button>
                    {bad && (
                      <button data-testid={`retry-${d.delivery_id}`} onClick={() => retry(d)} disabled={retrying === d.delivery_id} className="span-btn h-6 text-[10px]">
                        <ArrowClockwise size={10}/> {retrying === d.delivery_id ? "…" : "Retry"}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {detail && <DetailModal d={detail} onClose={() => setDetail(null)}/>}
    </div>
  );
}

function DetailModal({ d, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="webhook-detail-modal">
      <div className="bg-[var(--span-surface)] border border-[var(--span-border-strong)] w-full max-w-2xl max-h-[80vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="label-eyebrow">Delivery detail · {d.delivery_id}</div>
          <button onClick={onClose}><XIcon size={16}/></button>
        </div>
        <div className="space-y-3 text-[12px]">
          <div><span className="text-[var(--span-text-2)]">Event:</span> <span className="font-mono">{d.event}</span></div>
          <div><span className="text-[var(--span-text-2)]">URL:</span> <span className="font-mono break-all">{d.url}</span></div>
          <div><span className="text-[var(--span-text-2)]">Status:</span> {d.status} · attempts {d.attempts}</div>
          {d.last_response_code && (
            <div><span className="text-[var(--span-text-2)]">Last HTTP:</span> {d.last_response_code}</div>
          )}
          {d.last_error && (
            <div className="border-l-4 border-[var(--span-danger)] pl-2 font-mono text-[11px] text-[var(--span-danger)]">{d.last_error}</div>
          )}
          {d.last_response_excerpt && (
            <div>
              <div className="label-eyebrow mb-1">Response excerpt</div>
              <pre className="bg-[var(--span-surface-2)] p-2 text-[10px] overflow-auto max-h-32">{d.last_response_excerpt}</pre>
            </div>
          )}
          <div>
            <div className="label-eyebrow mb-1">Payload</div>
            <pre className="bg-[var(--span-surface-2)] p-2 text-[10px] overflow-auto max-h-64">{JSON.stringify(d.payload, null, 2)}</pre>
          </div>
        </div>
      </div>
    </div>
  );
}
